import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Printer, Zap } from 'lucide-react';
import { generateBulkBarcodes } from '@/data/mockAssets';
import { toast } from 'sonner';

interface BulkSerialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated?: (codes: string[]) => void;
}

export default function BulkSerialDialog({ open, onOpenChange, onGenerated }: BulkSerialDialogProps) {
  const [quantity, setQuantity] = useState(10);
  const [generated, setGenerated] = useState<string[]>([]);

  function handleGenerate() {
    if (quantity <= 0 || quantity > 500) return;
    const codes = generateBulkBarcodes(quantity);
    setGenerated(codes);
    onGenerated?.(codes);
    toast.success(`Generated ${codes.length} serial numbers`);
  }

  function handlePrintAll() {
    toast.success(`Printing ${generated.length} labels...`);
  }

  function handleClose() {
    setGenerated([]);
    setQuantity(10);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Bulk Serial Generation
          </DialogTitle>
        </DialogHeader>

        {generated.length === 0 ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Quantity to Generate</Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                className="text-base h-12"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Max 500 per batch</p>
            </div>
            <Button onClick={handleGenerate} disabled={quantity <= 0 || quantity > 500} className="w-full gap-2">
              <Zap className="h-4 w-4" /> Generate {quantity} Serials
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">{generated.length} serials generated</p>
              <Button variant="outline" size="sm" onClick={handlePrintAll} className="gap-1.5">
                <Printer className="h-3.5 w-3.5" /> Print All Labels
              </Button>
            </div>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-border p-2 space-y-1">
              {generated.map((code) => (
                <div key={code} className="flex items-center justify-between py-1 px-2 rounded bg-muted/50">
                  <span className="text-sm font-mono text-foreground">{code}</span>
                  <Badge variant="outline" className="text-[10px]">new</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {generated.length > 0 ? 'Done' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
